import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { Clock, AlertTriangle, RefreshCw, Trash2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function fmtHours(h) {
  if (h == null) return "—";
  if (h < 24) return `${h.toFixed(1)}h`;
  const d = Math.floor(h / 24); const rest = (h - d * 24).toFixed(1);
  return `${d}d ${rest}h`;
}

export default function OldShifts() {
  const [hours, setHours] = useState(12);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [closing, setClosing] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/time-tracking/old-shifts?hours=${hours}`);
      setData(r.data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Erro"); }
    finally { setLoading(false); }
  }, [hours]);

  useEffect(() => { load(); }, [load]);

  const endAllOld = async () => {
    if (!data || data.count === 0) { toast.info("Nada para fechar"); return; }
    if (!window.confirm(`Fechar ${data.count} turno(s) aberto(s) há mais de ${hours}h?\n\nCada turno será fechado assumindo 8h de trabalho (não a hora atual), para não inflacionar horas.`)) return;
    setClosing(true);
    try {
      const r = await axios.post(`${API}/time-tracking/end-old-shifts?hours=${hours}`);
      toast.success(`${r.data.count} turno(s) antigos encerrados`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erro"); }
    finally { setClosing(false); }
  };

  return (
    <div className="space-y-4" data-testid="old-shifts-page">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-2">
            <Clock className="h-8 w-8 text-amber-600" /> Turnos Antigos por Fechar
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Turnos que ficaram abertos e nunca foram encerrados (esquecimento do operador ou falha de sistema).
            O fecho assume 8h de trabalho para não inflacionar horas.
          </p>
        </div>
        <div className="flex items-end gap-2">
          <div>
            <Label className="text-xs">Turnos abertos há mais de</Label>
            <div className="flex items-center gap-1 mt-1">
              <Input type="number" min={1} max={720} value={hours} onChange={(e) => setHours(parseInt(e.target.value) || 12)}
                     className="w-24" data-testid="old-shifts-hours-input" />
              <span className="text-sm text-slate-500">horas</span>
            </div>
          </div>
          <Button variant="outline" onClick={load} data-testid="old-shifts-refresh">
            <RefreshCw className={`h-4 w-4 mr-1 ${loading ? "animate-spin" : ""}`} /> Atualizar
          </Button>
          <Button
            onClick={endAllOld}
            disabled={!data || data.count === 0 || closing}
            className="bg-red-600 hover:bg-red-700 text-white font-semibold shadow-md"
            data-testid="close-all-old-shifts-btn"
          >
            <Trash2 className="h-4 w-4 mr-1" />
            {closing ? "A fechar…" : `Fechar TODOS os antigos (${data?.count || 0})`}
          </Button>
        </div>
      </div>

      {data && data.count === 0 && (
        <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-8 text-center">
          <div className="text-emerald-700 font-semibold text-lg">✅ Sem turnos antigos por fechar</div>
          <div className="text-emerald-600 text-sm mt-1">Todos os turnos com mais de {hours}h estão encerrados.</div>
        </div>
      )}

      {data && data.count > 0 && (
        <div className="bg-white border rounded-lg overflow-hidden">
          <div className="p-3 border-b bg-amber-50 flex items-center gap-2 text-amber-800">
            <AlertTriangle className="h-5 w-5" />
            <span className="font-semibold">{data.count} turno(s) aberto(s) há mais de {hours}h</span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50">
                <tr>
                  <th className="text-left p-2">Colaborador</th>
                  <th className="text-left p-2">Código</th>
                  <th className="text-left p-2">Papel</th>
                  <th className="text-left p-2">Entrada</th>
                  <th className="text-right p-2">Abertura há</th>
                  <th className="text-left p-2">Turno</th>
                </tr>
              </thead>
              <tbody>
                {data.records.map(r => (
                  <tr key={r.id} className="border-t hover:bg-slate-50" data-testid={`old-shift-${r.id}`}>
                    <td className="p-2 font-medium">{r.user_name || "—"}</td>
                    <td className="p-2 font-mono text-xs">{r.user_code}</td>
                    <td className="p-2 text-xs">{r.user_role}</td>
                    <td className="p-2 text-xs">{r.clock_in ? new Date(r.clock_in).toLocaleString("pt-PT") : "—"}</td>
                    <td className="p-2 text-right font-bold" style={{ color: r.hours_open > 48 ? "#dc2626" : r.hours_open > 24 ? "#ea580c" : "#d97706" }}>
                      {fmtHours(r.hours_open)}
                    </td>
                    <td className="p-2 text-xs">{r.shift || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
